import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { PRICING, BADGE_URL, JAZMIN_AVATAR_URL, CDA_CHALLENGES, CARE_COMPLY_PROVIDES } from '../../data/cdaContent';
import { FOUNDER } from '../../data/workforceContent';
import { CdaLogo } from '../../components/cda/CdaBrand';
import { useLang, LanguageBar } from '../../i18n/cda';
import { GraduationCap, CheckCircle, Sparkles, Award, Users, FileCheck, HeartHandshake, AlertCircle, DollarSign, Baby, Briefcase, ExternalLink, LayoutDashboard } from 'lucide-react';
import EmailCapturePopup from '../../components/cda/EmailCapturePopup';

const HERO = "https://static.prod-images.emergentagent.com/jobs/574f4e75-09df-4f34-882a-f8d9217d04a9/images/ce1803be469025ac3872d6e18a4f7b49aa1d4f9785a646093a192adfd29e397c.png";
const JAZMIN = JAZMIN_AVATAR_URL;

const FEATURES = [
  { icon: GraduationCap, title: "Step-by-step CDA roadmap", desc: "From your first training hour to your credential in hand — never wonder what's next." },
  { icon: Award, title: "120 + 480 hour trackers", desc: "Track your education and classroom experience hours as you go." },
  { icon: FileCheck, title: "Portfolio binder builder", desc: "Printable section covers and a checklist for observation day." },
  { icon: Users, title: "Find your PD Specialist", desc: "San Antonio observers ready to schedule your verification visit." },
  { icon: HeartHandshake, title: "Funding help", desc: "GoFundMe and T.E.A.C.H. Texas scholarship guidance for the $525 fee." },
  { icon: Sparkles, title: "Jazmin, your AI coach", desc: "A warm career coach in your pocket, day or night." },
];

const formatApiError = (detail) => {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return '';
};

const CandidateLanding = () => {
  const navigate = useNavigate();
  const { login, signupCandidate, user } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState('signup');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);

  React.useEffect(() => {
    if (user?.role === 'candidate') navigate('/cda/journey');
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailTaken(false);

    if (mode === 'signup') {
      if (!form.name.trim()) { setError(t('Please enter your name.')); return; }
      if (form.password.length < 6) { setError(t('Password must be at least 6 characters.')); return; }
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        await signupCandidate(form.name, form.email, form.password);
        toast.success(t('Welcome to Care Comply! 🌟'));
      } else {
        await login(form.email, form.password);
        toast.success(t('Welcome back!'));
      }
      navigate('/cda/journey');
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      if (detail.toLowerCase().includes('already registered')) {
        setEmailTaken(true);
        setError(t('This email is already registered. Log in instead.'));
      } else if (detail.toLowerCase().includes('incorrect')) {
        setError(t('Incorrect email or password.'));
      } else {
        setError(detail || t('Something went wrong. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <LanguageBar />
      {/* Nav */}
      <header className="px-5 sm:px-10 py-5 flex items-center justify-between">
        <CdaLogo size={40} />
        <div className="flex items-center gap-4">
          {user?.role === 'admin' && (
            <button 
              onClick={() => navigate('/cda-candidates')} 
              className="flex items-center gap-1.5 text-sm font-medium bg-[#0d7d6f] text-white px-3 py-1.5 rounded-lg hover:bg-[#0a6358] transition-colors" 
              data-testid="admin-panel-link"
            >
              <LayoutDashboard className="w-4 h-4" /> Admin Panel
            </button>
          )}
          <button onClick={() => navigate('/cda/centers')} className="text-sm font-medium text-slate-600 hover:text-[#0d7d6f] hidden sm:inline" data-testid="nav-centers-link">
            {t('For centers')}
          </button>
          <button onClick={() => { setMode('login'); document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-sm font-medium text-[#0d7d6f] hover:underline" data-testid="nav-login-link">
            {t('Log in')}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-5 sm:px-10 pt-6 pb-16 grid lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0d7d6f]/10 text-[#0d7d6f] text-sm font-medium mb-5">
            <Sparkles className="w-4 h-4" /> {t('Built for frontline child development teachers')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('Complete Your CDA Journey with')} <span className="text-[#c4622d]">{t('Care Comply')}</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-lg">
            {t('Personalized CDA career coaching, 480-hour experience guidance, portfolio support, and employment preparation for future early childhood educators in San Antonio and across Texas.')}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button onClick={() => { setMode('signup'); document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth' }); }} className="h-12 px-8 rounded-full text-base bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="hero-start-button">
              {t('Start your CDA journey')}
            </Button>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-2xl font-bold text-slate-900">{PRICING.monthlyPrice}</span>{t('/mo')}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">🎟️ {t('Use code')} <span className="font-semibold text-[#0d7d6f]">{PRICING.promoCode}</span> {t('for 50% off your first month.')}</p>
          <p className="text-xs text-slate-500 mt-1">{t(PRICING.note)}</p>
        </div>

        {/* Auth card */}
        <div id="auth-card" className="bg-white rounded-2xl shadow-xl border border-slate-100 p-7 max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <img src={JAZMIN} alt="Jazmin" className="w-12 h-12 rounded-full object-cover" />
            <div>
              <p className="font-semibold text-slate-900">{mode === 'signup' ? t('Create your account') : t('Welcome back')}</p>
              <p className="text-xs text-slate-500">{t("Jazmin is ready to help you — you've got this!")}</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <Label>{t('Full name')}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 h-11" placeholder="Maria Hernandez" data-testid="signup-name-input" />
              </div>
            )}
            <div>
              <Label>{t('Email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setError(''); }} required className="mt-1.5 h-11" placeholder="you@email.com" data-testid="auth-email-input" />
            </div>
            <div>
              <Label>{t('Password')}</Label>
              <Input type="password" value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); setError(''); }} required minLength={6} className="mt-1.5 h-11" placeholder="••••••••" data-testid="auth-password-input" />
              {mode === 'signup' && <p className="text-xs text-slate-400 mt-1">{t('At least 6 characters')}</p>}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 flex items-start gap-2" data-testid="auth-error">
                <span>{error}</span>
                {emailTaken && (
                  <button type="button" onClick={() => { setMode('login'); setError(''); setEmailTaken(false); }} className="font-semibold underline shrink-0" data-testid="switch-to-login">
                    {t('Log in')}
                  </button>
                )}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="auth-submit-button">
              {loading ? t('Please wait...') : mode === 'signup' ? t('Create account & continue') : t('Log in')}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-600 mt-4">
            {mode === 'signup' ? t('Already have an account?') : t('New here?')}{' '}
            <button onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); setError(''); setEmailTaken(false); }} className="text-[#0d7d6f] font-medium hover:underline" data-testid="auth-toggle-mode">
              {mode === 'signup' ? t('Log in') : t('Create one')}
            </button>
          </p>
        </div>
      </section>

      {/* How it works — easy 1-2-3 */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 pb-14" data-testid="how-it-works">
        <p className="text-center text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('How it works')}</p>
        <h2 className="text-center text-3xl font-bold text-slate-900 mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('Your CDA, in 3 simple steps')}
        </h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { n: 1, title: t('Sign up in seconds'), desc: t('Create your account and unlock your guided CDA roadmap.') },
            { n: 2, title: t('Follow your guided journey'), desc: t('Track your hours, build your binder, and let Jazmin coach you every step.') },
            { n: 3, title: t('Get your CDA credential'), desc: t('Apply, get observed, upload your credential — and celebrate!') },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-2xl border border-slate-100 p-7 text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-[#0d7d6f] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {s.n}
              </div>
              <h3 className="font-bold text-slate-900 mb-1.5">{s.title}</h3>
              <p className="text-sm text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button onClick={() => { setMode('signup'); document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth' }); }} className="h-12 px-8 rounded-full text-base bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="how-start-button">
            {t('Start your CDA journey')}
          </Button>
        </div>
      </section>

      {/* What is a CDA? */}
      <section className="bg-white py-14" data-testid="what-is-cda-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-10">
          <div className="text-center mb-10">
            <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('What is a CDA?')}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('The Child Development Associate Credential')}
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              {t('The CDA (Child Development Associate) is the most widely recognized credential in early childhood education. It proves you have the skills and knowledge to nurture the development of young children. Earning your CDA opens doors to better jobs, higher pay, and a rewarding career helping children grow.')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#fbf8f3] rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-[#0d7d6f] mb-2">120+</div>
              <p className="text-sm text-slate-600">{t('Hours of professional education required')}</p>
            </div>
            <div className="bg-[#fbf8f3] rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-[#0d7d6f] mb-2">480+</div>
              <p className="text-sm text-slate-600">{t('Hours of hands-on experience with children')}</p>
            </div>
            <div className="bg-[#fbf8f3] rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-[#c4622d] mb-2">$525</div>
              <p className="text-sm text-slate-600">{t('CDA Council assessment fee (scholarships available)')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CDA Requirements */}
      <section className="bg-[#0d7d6f]/[0.04] py-14" data-testid="cda-requirements-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-10">
          <div className="text-center mb-10">
            <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('Before You Start')}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('CDA Eligibility Requirements')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('To earn your CDA credential, you must meet these basic requirements set by the CDA Council and Texas licensing.')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0d7d6f]/10 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-[#0d7d6f]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">{t('Be at least 18 years old')}</h3>
                <p className="text-sm text-slate-600">{t('You must be 18 or older to apply for the CDA credential.')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0d7d6f]/10 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-[#0d7d6f]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">{t('High school diploma or GED')}</h3>
                <p className="text-sm text-slate-600">{t('A high school diploma, GED, or enrolled in a high school career program.')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0d7d6f]/10 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-[#0d7d6f]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">{t('120 hours of formal education')}</h3>
                <p className="text-sm text-slate-600">{t('Complete 120 clock hours of professional early childhood education training.')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0d7d6f]/10 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-[#0d7d6f]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">{t('480 hours of experience')}</h3>
                <p className="text-sm text-slate-600">{t('Work with children in a childcare setting within the past 3 years.')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <p className="text-slate-600 mb-4">{t('For complete requirements and the most up-to-date information:')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://www.cdacouncil.org/en/earn-a-cda/" target="_blank" rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0d7d6f] text-white rounded-full text-sm font-medium hover:bg-[#0a6358] transition-colors">
                {t('CDA Council Official Requirements')} <ExternalLink className="w-4 h-4" />
              </a>
              <a href="https://www.hhs.texas.gov/providers/protective-services-providers/child-care-licensing" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-slate-200 transition-colors">
                {t('Texas Child Care Licensing')} <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Hero image band */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 mb-12">
        <img src={HERO} alt="Childcare teacher with toddlers" className="w-full rounded-2xl object-cover max-h-[360px]" />
      </section>

      {/* Common challenges */}
      <section className="max-w-5xl mx-auto px-5 sm:px-10 pb-14" data-testid="challenges-section">
        <p className="text-center text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t("You're not alone")}</p>
        <h2 className="text-center text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Common challenges CDA candidates face')}</h2>
        <p className="text-center text-slate-600 max-w-2xl mx-auto mb-8">{t('Many aspiring childcare professionals face barriers that have nothing to do with their ability to become great teachers. The journey often stops not from a lack of passion, but a lack of access and support.')}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {CDA_CHALLENGES.map((c) => (
            <div key={c} className="flex items-start gap-3 bg-white rounded-xl border border-slate-100 p-4">
              <AlertCircle className="w-5 h-5 text-[#c4622d] shrink-0 mt-0.5" />
              <span className="text-sm text-slate-700">{t(c)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* What Care Comply provides */}
      <section className="bg-[#0d7d6f]/[0.04] py-14" data-testid="provides-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-10">
          <h2 className="text-center text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('What Care Comply provides')}</h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-8">{t('We help current and future CDA candidates navigate the entire credentialing journey — wherever they are.')}</p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl mx-auto">
            {CARE_COMPLY_PROVIDES.map((p) => (
              <div key={p} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0" />
                <span className="text-slate-700">{t(p)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shareable badge showcase */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 mb-16">
        <div className="bg-gradient-to-r from-[#0d7d6f] to-[#0a6358] rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-8 overflow-hidden relative">
          <img src={BADGE_URL} alt="I'm earning my CDA badge" className="w-40 h-40 rounded-2xl object-cover shadow-2xl shrink-0" style={{ animation: 'float 4s ease-in-out infinite' }} />
          <div className="text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('Wear your progress with pride.')}
            </h2>
            <p className="text-emerald-100 max-w-lg">
              {t('Every member gets a shareable "I\'m Earning My CDA" badge to post on Facebook & Instagram — celebrate your journey and rally your community (perfect alongside your GoFundMe).')}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 pb-20">
        <h2 className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('Everything you need in one place')}</h2>
        <p className="text-3xl font-bold text-slate-900 mb-10 max-w-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('The CDA process is confusing. We make it simple.')}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-xl border border-slate-100 p-6 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-lg bg-[#0d7d6f]/10 flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-[#0d7d6f]" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1.5">{t(f.title)}</h3>
              <p className="text-sm text-slate-600">{t(f.desc)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* For Childcare Centers */}
      <section className="max-w-5xl mx-auto px-5 sm:px-10 py-14" data-testid="centers-section">
        <div className="bg-white rounded-2xl border border-slate-100 p-8 sm:p-10 flex flex-col md:flex-row items-center gap-8">
          <div className="w-16 h-16 rounded-2xl bg-[#0d7d6f]/10 flex items-center justify-center shrink-0">
            <HeartHandshake className="w-8 h-8 text-[#0d7d6f]" />
          </div>
          <div className="flex-1">
            <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('For Childcare Centers')}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Build your team with motivated CDA candidates')}</h2>
            <p className="text-slate-600">{t('Hiring and mentoring future CDA educators strengthens your workforce and creates pathways for quality improvement. Care Comply connects your center with motivated candidates ready to learn, grow, and contribute to high-quality early childhood programs.')}</p>
            <Button onClick={() => navigate('/cda/centers')} className="mt-5 h-11 px-6 rounded-full bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="centers-cta-button">
              {t('Partner with Care Comply')}
            </Button>
          </div>
        </div>
      </section>

      {/* What You Get Access To */}
      <section className="max-w-5xl mx-auto px-5 sm:px-10 py-14" data-testid="member-benefits-section">
        <div className="text-center mb-10">
          <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('What You Get When You Join')}</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Everything you need to succeed')}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">{t('Once you sign up, you\'ll get instant access to all resources, tools, and support to complete your CDA journey.')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Workforce Resources Preview */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="workforce-resources-section">
            <div className="w-12 h-12 rounded-xl bg-[#0d7d6f]/10 flex items-center justify-center mb-4">
              <Briefcase className="w-6 h-6 text-[#0d7d6f]" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Workforce Resources')}</h3>
            <p className="text-sm text-slate-600 mb-4">{t('Build the professional skills employers want')}</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Professional appearance guides')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Interview preparation tips')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Resume building resources')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Workplace communication skills')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Training videos & articles')}</li>
            </ul>
          </div>

          {/* Childcare for Teachers Preview */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="childcare-for-teachers-section">
            <div className="w-12 h-12 rounded-xl bg-[#0d7d6f]/10 flex items-center justify-center mb-4">
              <Baby className="w-6 h-6 text-[#0d7d6f]" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Childcare for Your Children')}</h3>
            <p className="text-sm text-slate-600 mb-4">{t('Find care while you build your career')}</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Subsidized childcare programs')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Head Start & Early Head Start info')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Quality childcare finder tools')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Local Pre-K programs')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Employer childcare benefits tips')}</li>
            </ul>
          </div>

          {/* Funding Assistance Preview */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="funding-assistance-section">
            <div className="w-12 h-12 rounded-xl bg-[#c4622d]/10 flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-[#c4622d]" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Payment & Funding Help')}</h3>
            <p className="text-sm text-slate-600 mb-4">{t('Ways to fund your CDA journey')}</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Scholarship opportunities')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Employer reimbursement guides')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Workforce training grants')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Payment plan options')}</li>
              <li className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-[#0d7d6f]" /> {t('Fundraising tips')}</li>
            </ul>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-slate-500 mb-4">{t('Sign up to unlock all resources and start your CDA journey today')}</p>
        </div>
      </section>

      {/* About the founder */}
      <section className="max-w-4xl mx-auto px-5 sm:px-10 py-14" data-testid="founder-section">
        <div className="bg-white rounded-2xl border border-slate-100 p-8 sm:p-10 flex flex-col md:flex-row gap-8 items-center">
          <div className="shrink-0 text-center">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#0d7d6f] to-[#0a6358] text-white flex items-center justify-center text-3xl font-bold mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>{FOUNDER.initials}</div>
            <p className="font-bold text-slate-900 mt-3">{FOUNDER.name}</p>
            <p className="text-xs text-slate-500">{FOUNDER.title}</p>
          </div>
          <div className="flex-1">
            <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('Meet the founder')}</p>
            <p className="text-slate-600 mb-4">{FOUNDER.bio}</p>
            <blockquote className="border-l-4 border-[#0d7d6f] pl-4 italic text-slate-700">"{FOUNDER.quote}"</blockquote>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-4xl mx-auto px-5 sm:px-10 pb-12 text-center" data-testid="mission-section">
        <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-3">{t('Our mission')}</p>
        <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('To remove barriers, strengthen workforce pathways, and create opportunities for aspiring early childhood educators — wherever they are in their CDA journey. Because every future teacher deserves a pathway to success.')}
        </p>
      </section>

      {/* Testimonials / Social Proof */}
      <section className="bg-white py-14" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-10">
          <div className="text-center mb-10">
            <p className="text-base md:text-lg font-semibold text-[#c4622d] mb-2">{t('What Our Community Says')}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('Trusted by CDA Candidates Across Texas')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#fbf8f3] rounded-xl p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 mb-4 italic">"Care Comply made the CDA process so much easier to understand. Jazmin answered all my questions about the portfolio!"</p>
              <p className="font-semibold text-slate-900">Maria G.</p>
              <p className="text-sm text-slate-500">San Antonio, TX</p>
            </div>
            <div className="bg-[#fbf8f3] rounded-xl p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 mb-4 italic">"I was nervous about starting my CDA journey, but the step-by-step guidance gave me confidence. Now I'm halfway there!"</p>
              <p className="font-semibold text-slate-900">Rosa T.</p>
              <p className="text-sm text-slate-500">Austin, TX</p>
            </div>
            <div className="bg-[#fbf8f3] rounded-xl p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 mb-4 italic">"The workforce readiness training helped me prepare for my interview. I got hired at my dream center!"</p>
              <p className="font-semibold text-slate-900">Ana L.</p>
              <p className="text-sm text-slate-500">Houston, TX</p>
            </div>
          </div>
          <p className="text-center text-sm text-slate-400 mt-6">
            {t('Have you used Care Comply?')} <a href="https://g.page/r/CareComplyCDA/review" target="_blank" rel="noopener noreferrer" className="text-[#0d7d6f] hover:underline">{t('Leave us a Google review!')}</a>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0d7d6f] py-16 px-5 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('Your community needs you credentialed.')}
        </h2>
        <p className="text-emerald-100 max-w-xl mx-auto mb-8">
          {t('Join the teachers turning their calling into a credential.')} {PRICING.monthlyPrice}{t('/mo')} · {t('50% off your first month with code')} {PRICING.promoCode}
        </p>
        <Button onClick={() => { setMode('signup'); document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth' }); }} className="h-12 px-8 rounded-full text-base bg-white text-[#0d7d6f] hover:bg-emerald-50" data-testid="cta-start-button">
          {t('Begin now')}
        </Button>
      </section>

      <footer className="py-8 text-center text-sm text-slate-500">
        {t('Care Comply is a service of Barro Roots Contracting. • CDA® is a trademark of the Council for Professional Recognition.')}
      </footer>

      {/* Email Capture Popup - shows after 8 seconds for non-logged-in visitors */}
      {!user && <EmailCapturePopup delay={8000} />}
    </div>
  );
};

export default CandidateLanding;
